import { useQuery } from '@tanstack/react-query';
import { APPOINTMENT_BOOKING_API_BASE_URL } from '../constants/constants';
import type { AppointmentBookingSettings } from '../types';
import { isCalendarIntegrationEnabled } from './is-calendar-integration-enabled';

export type CalendarMeta = {
  description?: string;
  id: string;
  summary: string;
  timeZone: string;
};

interface useCalendarMetaQueryProps {
  integrationGoogleCalendarAuth: AppointmentBookingSettings['integrationGoogleCalendarAuth'];
  integrationGoogleCalendarCalendar: AppointmentBookingSettings['integrationGoogleCalendarCalendar'];
  widgetToken: AppointmentBookingSettings['widgetToken'];
}

export const useCalendarMetaQuery = ({
  integrationGoogleCalendarAuth,
  integrationGoogleCalendarCalendar,
  widgetToken
}: useCalendarMetaQueryProps) => {
  const connectionPid = integrationGoogleCalendarAuth?.identity;
  const calendarId = integrationGoogleCalendarCalendar?.id;
  const isGCEnabled = isCalendarIntegrationEnabled({
    integrationGoogleCalendarAuth,
    integrationGoogleCalendarCalendar
  });

  return useQuery<CalendarMeta | undefined>({
    queryKey: ['calendar-meta-info', connectionPid, calendarId, isGCEnabled],
    queryFn: async () => {
      const calendarIdEncoded = encodeURIComponent(calendarId!);
      const calendarConnectionPid = encodeURIComponent(connectionPid!);

      const params = new URLSearchParams({
        connection: calendarConnectionPid
      });

      const response = await fetch(
        `${APPOINTMENT_BOOKING_API_BASE_URL}/google-calendar/calendar/${calendarIdEncoded}?${params}`,
        {
          method: 'GET',
          headers: new Headers({
            'x-widget-token': widgetToken || ''
          })
        }
      );

      const data = (await response.json()) as unknown as {
        payload: CalendarMeta;
        code: number;
        message: string;
      };

      return data.payload;
    },
    enabled: [isGCEnabled, connectionPid, calendarId, widgetToken].every(
      Boolean
    ),
    refetchOnWindowFocus: true
  });
};
